import { Controller } from "@hotwired/stimulus"
import { computePosition, shift, offset, arrow } from "@floating-ui/dom"

export default class extends Controller {
  static targets = ["trigger", "content", "arrow"]
  static values = {
    delayDuration: { type: Number, default: 700 },
    skipDelayDuration: { type: Number, default: 300 },
    open: { type: Boolean, default: false },
    side: { type: String, default: "top" },
    align: { type: String, default: "center" },
    sideOffset: { type: Number, default: 8 }
  }

  connect() {
    this.openTimeout = null
    this.closeTimeout = null
    this.skipDelay = false
    this.updateVisibility()
  }

  disconnect() {
    this.clearTimeouts()
  }

  // Trigger hover events
  triggerMouseEnter() {
    this.clearTimeouts()
    const delay = this.skipDelay ? this.skipDelayDurationValue : this.delayDurationValue

    this.openTimeout = setTimeout(() => {
      this.open()
    }, delay)
  }

  triggerMouseLeave() {
    this.clearTimeouts()
    this.close()
  }

  // Trigger focus events
  triggerFocus() {
    this.clearTimeouts()
    // Focus opens immediately (no delay)
    this.open()
  }

  triggerBlur() {
    this.clearTimeouts()
    this.close()
  }

  // Keyboard interactions
  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    } else if (event.key === " " || event.key === "Enter") {
      // Close tooltip when trigger is activated
      event.preventDefault()
      this.close()
    }
  }

  // Open/close methods
  open() {
    if (!this.openValue) {
      this.openValue = true
      this.updateVisibility()
      this.updatePosition()
      this.skipDelay = true

      // Reset skipDelay after skipDelayDuration
      this.closeTimeout = setTimeout(() => {
        this.skipDelay = false
      }, this.skipDelayDurationValue)
    }
  }

  close() {
    if (this.openValue) {
      this.openValue = false
      this.updateVisibility()
    }
  }

  // Update DOM
  updateVisibility() {
    if (this.openValue) {
      this.contentTarget.classList.remove("hidden")
      this.contentTarget.classList.add("block")
      this.triggerTarget.setAttribute("aria-describedby", this.contentTarget.id)
    } else {
      this.contentTarget.classList.remove("block")
      this.contentTarget.classList.add("hidden")
      this.triggerTarget.removeAttribute("aria-describedby")
    }
  }

  // Update position using Floating UI
  async updatePosition() {
    if (!this.hasContentTarget) return

    const placement = this.getPlacement()
    const currentSide = placement.split('-')[0]
    const align = this.alignValue

    const middleware = [
      offset(this.sideOffsetValue),
      shift({ padding: 8 })
    ]

    // Add arrow middleware if arrow exists
    if (this.hasArrowTarget) {
      middleware.push(arrow({ element: this.arrowTarget }))
    }

    const { x, y, placement: finalPlacement, middlewareData } = await computePosition(
      this.triggerTarget,
      this.contentTarget,
      {
        placement,
        middleware,
        strategy: 'fixed'
      }
    )

    let finalX = x
    let finalY = y

    // Calculate anchor point and adjust tooltip position
    if (this.hasArrowTarget) {
      const triggerRect = this.triggerTarget.getBoundingClientRect()
      const arrowOffsetFromEdge = 11.51
      const arrowHalfSize = 6

      if (currentSide === 'left' || currentSide === 'right') {
        // For left/right: don't adjust position
        // Arrow will be positioned to point to trigger center dynamically
        // (handled in arrow positioning section below)

      } else if (currentSide === 'top' || currentSide === 'bottom') {
        // For top/bottom with left/right align: anchor point is offset from trigger edge
        if (align === 'left') {
          const anchorX = triggerRect.left + arrowOffsetFromEdge + arrowHalfSize
          const arrowCenterInTooltip = arrowOffsetFromEdge + arrowHalfSize

          // Adjust X position so arrow points to anchor
          finalX = anchorX - arrowCenterInTooltip

        } else if (align === 'right') {
          const anchorX = triggerRect.right - arrowOffsetFromEdge - arrowHalfSize
          const arrowCenterInTooltip = arrowOffsetFromEdge + arrowHalfSize
          const contentRect = this.contentTarget.getBoundingClientRect()

          // Adjust X position so arrow points to anchor (from right edge)
          finalX = anchorX - (contentRect.width - arrowCenterInTooltip)
        }
        // For center align, use Floating UI's calculated position (no adjustment)
      }
    }

    // Apply position to content
    Object.assign(this.contentTarget.style, {
      position: 'fixed',
      left: `${finalX}px`,
      top: `${finalY}px`,
      margin: '0'
    })

    // Update arrow position if exists
    if (this.hasArrowTarget && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow
      const currentSide = finalPlacement.split('-')[0]
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[currentSide]

      const align = this.alignValue

      // For left/right tooltips, position arrow to point to trigger center
      if (currentSide === 'left' || currentSide === 'right') {
        const triggerRect = this.triggerTarget.getBoundingClientRect()
        const contentRect = this.contentTarget.getBoundingClientRect()

        // Calculate trigger center Y position
        const triggerCenterY = triggerRect.top + (triggerRect.height / 2)

        // Calculate arrow position relative to tooltip content
        const arrowTopPosition = triggerCenterY - contentRect.top - 6 // 6px = half arrow height

        Object.assign(this.arrowTarget.style, {
          left: '',
          right: '',
          top: `${arrowTopPosition}px`,
          bottom: '',
          [staticSide]: '-4px'
        })

      } else {
        // For top/bottom tooltips, position arrow based on align
        Object.assign(this.arrowTarget.style, {
          left: '',
          right: '',
          top: '',
          bottom: '',
          [staticSide]: '-4px'
        })

        // Position arrow based on alignment
        if (align === 'left') {
          // Arrow positioned 11.51px from left edge
          this.arrowTarget.style.left = '11.51px'
        } else if (align === 'right') {
          // Arrow positioned 11.51px from right edge
          this.arrowTarget.style.right = '11.51px'
        } else {
          // Center - use Floating UI's calculated position
          this.arrowTarget.style.left = arrowX != null ? `${arrowX}px` : ''
        }
      }
    }
  }

  // Get Floating UI placement from side and align values
  getPlacement() {
    const side = this.sideValue
    const align = this.alignValue

    if (align === "center" || align === "none") {
      return side
    }

    // Map align to Floating UI alignment
    const alignmentMap = {
      left: "start",
      right: "end"
    }

    return `${side}-${alignmentMap[align] || align}`
  }

  // Helper methods
  clearTimeouts() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout)
      this.openTimeout = null
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  }
}
